/**
 * React Starter Kit (https://www.reactstarterkit.com/)
 *
 * Copyright © 2014-present Kriasoft, LLC. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.txt file in the root directory of this source tree.
 */
import * as React from 'react'
import { HttpStatus } from '@shopping/common'
import Layout from 'pc/components/Common/Layout'
import { ActionParam } from 'common/types'
import typeChecker from 'common/typeChecker'
import { isBrowser } from 'common/utils'
import { fetchMember } from 'common/ducks/naverMember'
import { getSubVerticalByPath } from 'common/constants'
import { setSubVerticalByPathname } from 'common/ducks/subVerticalByPathname'

export const BasePath = {
  PRODUCT_AITEMS: '/:windowName/:subWindowName?/stores/:storeId/products/:productId/aitems',
  PRODUCT_QNA: '/:windowName/:subWindowName?/stores/:storeId/products/:productId/qna',
  PRODUCT_LOCATION: '/:windowName/:subWindowName?/stores/:storeId/products/:productId/locations/:addressId',
  PRODUCT_FROM_MOBILE: '/:windowName/:subWindowName?/stores/:storeId/products/:productId/:mobileOnly',
  PRODUCT: '/:windowName/:subWindowName?/stores/:storeId/products/:productId',
  RELATION_PRODUCT: '/(department|outlet)/stores/:storeId/relation-products/:relationProductNo',
  PRODUCT_CURATION: '/ext/:extensionType/:extensionKey',
  OUTLINK_PRODUCT: '/outlink/itemdetail/:productId',
  STORE_LOCATION: '/:windowName/:subWindowName?/stores/:storeId/location',
  STORE_WRONG_NUMBER_REPORT: '/report/wrongNumber/:storeId',
  STORE_DESIGNER: '/designer/stores/:storeId',
  STORE_FASHIONBRAND: '/style/fashionbrand/stores/:storeId',
  STORE_WITHOUTTAB: '/fresh/(localfood|directfarm|traditionalalcohol)/stores/:storeId',
  STORE_GENERAL:
    '/(department|outlet|beauty|style/style|fresh/homemeal|living/homeliving|living/handmade|living/stationery|kids|play|art|pet|foreign)/stores/:storeId',
  STORES: '/fresh/traditionalalcohol/stores',
  MY: '/my',
  POPUP: '/redirect/login',
  AUTH_POPUP: '/redirect/auth/:authType',
  REVIEW_REDIRECT: 'redirect/review/:reviewId',
  PRODUCT_REPORT: '/report',
  OUTLINK: '/outlink',
  WINDOW: '/windows/:windowType',
  EVENT:
    '/(department|outlet|style/style|living/homeliving|living/handmade|living/stationery|art|fresh/homemeal|fresh/traditionalalcohol|fresh/directfarm|fresh/localfood|foreign)/event',
  EVENT_EXHIBITION: '/(living/homeliving|living/handmade|living/stationery)/event/exhibition',
  EVENT_FLEAMARKET: '/living/handmade/event/fleamarket',
  BRANCH: '/(department|outlet)/branch/:branchId',
  MAP: '/style/style/map',
  HOME: '/:windowName/:subWindowName?/home',
  CATEGORY: '/:windowName/:subWindowName?/category/:layer1Id?',
  COORDI: '/style/style/coordi/:menuId',
  HANDMADE_SHOES: '/style/style/handmade-shoes',
  BRAND: '/:windowName/:subWindowName?/brand',
  HELP_POPUP: '/:windowName/:subWindowName?/help',
  PANEL_BRIDGE: '/panel/products/:productId',
  RECOMMEND_STORE: '/living/homeliving/recommend/stores',
  EXTERNAL_PRODUCTS: '/living/stationery/external/products',
  REVIEW: '/reviews',
  TAGPICK_TAG: '/tagpick/tags/:tagId',
  TAGPICK_MY_TAG: '/tagpick/my',
  LOCAL_MARKET: '/fresh/localfood/local-market',
  RECOMMEND_REVIEW: '/fresh/homemeal/recommend/reviews',
  NEWS_HOMEMEAL: '/fresh/homemeal/news',
  NEWS_DIRECT_FARM_LOCAL_FOOD: '/fresh/(directfarm|localfood)/news',
  HALL_OF_FAME: '/fresh/(localfood|directfarm)/hall-of-fame',
  NEWS_EVENT_DIRECT_FARM_LOCAL_FOOD: '/fresh/(directfarm|localfood)/news-event',
}

// The top-level (parent) route
export default {
  path: '/',

  // Keep in mind, routes are evaluated in order
  // code split 할 route 는 `codeSplitPath`, `importRoute` property object 로 설정, code split 하지 않을 route 는 `require(route 위치)` 로 설정
  // 로직은 universalRouter.ts 참고
  children: [
    {
      codeSplitPath: `${BasePath.PRODUCT_QNA}/(.*)*`,
      importRoute: () => import('./product/popup'),
    },
    {
      codeSplitPath: `${BasePath.PRODUCT_AITEMS}/(.*)*`,
      importRoute: () => import('./product/aiTems'),
    },
    {
      codeSplitPath: `${BasePath.PRODUCT_LOCATION}/(.*)*`,
      importRoute: () => import('./product/location'),
    },
    {
      codeSplitPath: `${BasePath.RELATION_PRODUCT}/(.*)*`,
      importRoute: () => import('./relationProduct'),
    },
    {
      codeSplitPath: [
        `${BasePath.PRODUCT_FROM_MOBILE}/(.*)*`,
        `${BasePath.PRODUCT}/(.*)*`,
        `${BasePath.PRODUCT_CURATION}/(.*)*`,
        `${BasePath.OUTLINK_PRODUCT}/(.*)*`,
      ],
      importRoute: () => import('./product'),
    },
    {
      codeSplitPath: `${BasePath.STORE_WRONG_NUMBER_REPORT}/(.*)*`,
      importRoute: () => import('./store/report'),
    },
    {
      codeSplitPath: `${BasePath.PRODUCT_REPORT}/(.*)*`,
      importRoute: () => import('./product/report'),
    },
    {
      codeSplitPath: `${BasePath.STORE_LOCATION}`,
      importRoute: () => import('./store/common'),
    },
    {
      codeSplitPath: `${BasePath.STORE_DESIGNER}/(.*)*`,
      importRoute: () => import('./store/designer'),
    },
    {
      codeSplitPath: `${BasePath.STORE_FASHIONBRAND}/(.*)*`,
      importRoute: () => import('./store/fashionBrand'),
    },
    {
      codeSplitPath: `${BasePath.STORE_WITHOUTTAB}/(.*)*`,
      importRoute: () => import('./store/withoutTab'),
    },
    {
      codeSplitPath: `${BasePath.STORE_GENERAL}/(.*)*`,
      importRoute: () => import('./store/general'),
    },
    {
      codeSplitPath: `${BasePath.STORES}/(.*)*`,
      importRoute: () => import('./store/index'),
    },
    {
      codeSplitPath: `${BasePath.MY}/(.*)*`,
      importRoute: () => import('./my'),
    },
    {
      codeSplitPath: `${BasePath.POPUP}/(.*)*`,
      importRoute: () => import('./popup'),
    },
    {
      codeSplitPath: `${BasePath.AUTH_POPUP}/(.*)*`,
      importRoute: () => import('./popup/auth'),
    },
    {
      codeSplitPath: `${BasePath.WINDOW}/(.*)*`,
      importRoute: () => import('./window'),
    },
    {
      codeSplitPath: [
        `${BasePath.EVENT}/(.*)*`,
        `${BasePath.EVENT_EXHIBITION}/(.*)*`,
        `${BasePath.EVENT_FLEAMARKET}/(.*)*`,
      ],
      importRoute: () => import('./event'),
    },
    {
      codeSplitPath: [`${BasePath.BRAND}/(.*)*`],
      importRoute: () => import('./brand'),
    },
    {
      codeSplitPath: [`${BasePath.HOME}/(.*)*`],
      importRoute: () => import('./home'),
    },
    {
      codeSplitPath: [`${BasePath.CATEGORY}/(.*)*`],
      importRoute: () => import('./category'),
    },
    {
      codeSplitPath: `${BasePath.COORDI}/(.*)*`,
      importRoute: () => import('./coordi'),
    },
    {
      codeSplitPath: `${BasePath.HANDMADE_SHOES}/(.*)*`,
      importRoute: () => import('./handmadeShoes'),
    },
    {
      codeSplitPath: `${BasePath.BRANCH}/(.*)*`,
      importRoute: () => import('./branch'),
    },
    {
      codeSplitPath: `${BasePath.MAP}/(.*)*`,
      importRoute: () => import('./map'),
    },
    {
      codeSplitPath: `${BasePath.HELP_POPUP}/(.*)*`,
      importRoute: () => import('./popup/help'),
    },
    {
      codeSplitPath: `${BasePath.PANEL_BRIDGE}/(.*)*`,
      importRoute: () => import('./shoppingPan'),
    },
    {
      codeSplitPath: `${BasePath.RECOMMEND_STORE}/(.*)*`,
      importRoute: () => import('./recommend/store'),
    },
    {
      codeSplitPath: `${BasePath.RECOMMEND_REVIEW}/(.*)*`,
      importRoute: () => import('./recommend/review'),
    },
    {
      codeSplitPath: `${BasePath.EXTERNAL_PRODUCTS}/(.*)*`,
      importRoute: () => import('./external'),
    },
    {
      codeSplitPath: `${BasePath.REVIEW}/(.*)*`,
      importRoute: () => import('./review'),
    },
    {
      codeSplitPath: [`${BasePath.TAGPICK_TAG}/(.*)*`, `${BasePath.TAGPICK_MY_TAG}/(.*)*`],
      importRoute: () => import('./tagPick'),
    },
    {
      codeSplitPath: `${BasePath.NEWS_HOMEMEAL}/(.*)*`,
      importRoute: () => import('./news/homemeal'),
    },
    {
      codeSplitPath: `${BasePath.NEWS_DIRECT_FARM_LOCAL_FOOD}/(.*)*`,
      importRoute: () => import('./news/directFarmLocalFood'),
    },
    {
      codeSplitPath: `${BasePath.HALL_OF_FAME}/(.*)*`,
      importRoute: () => import('./hallOfFame'),
    },
    {
      codeSplitPath: `${BasePath.NEWS_EVENT_DIRECT_FARM_LOCAL_FOOD}/(.*)*`,
      importRoute: () => import('./newsEvent/directFarmLocalFood'),
    },
    require('./outlink').default,
    // Wildcard routes, e.g. { path: '(.*)', ... } (must go last)
    require('./notFound').default,
  ],

  action: async ({ next, pathname, store, headers, curState }: ActionParam) => {
    await store.dispatch(setSubVerticalByPathname(pathname))
    await store.dispatch(fetchMember(headers))
    // Execute each child route until one of them return the result
    const childRouteResult = await next()
    const {
      component,
      title,
      description,
      redirect,
      hideLayout = false,
      hideHeaderFooter = false,
      httpStatus = HttpStatus.OK,
    } = childRouteResult

    if (isBrowser && typeChecker.isFunction(window['setGnbRedirectUrls'])) window['setGnbRedirectUrls']()

    if (redirect) {
      return childRouteResult
    }

    const isLogin = store.getState().naverMember.isLogin

    const product = store.getState().product[curState]
    const subVertical =
      getSubVerticalByPath(pathname) || (product && product.ssChannel && product.ssChannel.windowSubVertical)

    return {
      title,
      description,
      httpStatus,
      component: hideLayout ? (
        <>{component}</>
      ) : (
        <Layout {...{ pathname, subVertical, hideHeaderFooter }}>{component}</Layout>
      ),
    }
  },
}

// Provide default values for title, description etc.
// route.title = `${route.title || 'Untitled PageType'} - www.reactstarterkit.com`;
// route.description = route.description || '';
